import { definedMap, DefaultMap } from "@glideapps/ts-necessities";
import { Row } from "../../computation-model/data";
import { TableName } from "../../database-strings";
import { makeRowID } from "../../make-row-id";
import { fromPairs } from "lodash";

import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { panic } from "../../Support";
import {
    AllowedDataEdits,
    ComponentBuilder,
    ComponentConfiguratorContext,
    isNativeTableInAppDescriptionContext,
    PropertyDescriptor,
    SchemaInspector,
    WireActionHydrator,
    WireInflationBackend,
    WireValueGetter,
} from "../components";
import { makePropertyDescriptorsForColumns } from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import { getTableProperty, makeColumnProperty, makeTableProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    ColumnAssignment,
    ColumnAssignmentsWithLegacyDescription,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    findTable,
    getPrimitiveNonHiddenColumns,
    getTableColumn,
    getTableName,
    getTablesWithPrimitiveColumns,
    isColumnNonHidden,
    isComputedColumn,
    isPrimitiveType,
    makeTableRef,
    withOutputTable,
    TableGlideType,
} from "../description";
import { getColumnAssignmentsWithLegacy } from "../description-utils";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";

export interface AddRowActionDescription extends ActionDescription, ColumnAssignmentsWithLegacyDescription {
    readonly kind: ActionKind.AddRow;
    readonly tableName: PropertyDescription;
}

function getTable(
    desc: AddRowActionDescription,
    schema: SchemaInspector
): { tableName: TableName; table: TableGlideType } | undefined {
    const tableName = getTableProperty(desc.tableName);
    const table = schema.findTable(tableName);
    if (tableName === undefined || table === undefined) return undefined;
    return { tableName, table };
}

export function makeValueGettersForTable(
    ib: WireInflationBackend,
    columnAssignments: readonly ColumnAssignment[],
    table: TableGlideType
): readonly [string, WireValueGetter][] {
    const outputValueGetters: [string, WireValueGetter][] = [];
    for (const a of columnAssignments) {
        const column = getTableColumn(table, a.destColumn);
        if (column === undefined || !isPrimitiveType(column.type)) continue;

        const [getter, type] = ib.getValueGetterForProperty(a.value, false);
        if (!isPrimitiveType(type)) continue;
        outputValueGetters.push([a.destColumn, getter]);
    }
    return outputValueGetters;
}

export class AddRowActionHandler extends BaseActionHandler<AddRowActionDescription> {
    public readonly kind = ActionKind.AddRow;
    public readonly iconName = "plus-circle";

    public getDescriptor(
        desc: AddRowActionDescription | undefined,
        tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext
    ): ActionDescriptor {
        const hasUserFeature = ccc.userFeatures.addRowToSheet;
        const destTable = definedMap(desc, d =>
            definedMap(getTableProperty(d.tableName), t => findTable(ccc.schema, makeTableRef(t)))
        );
        let propertyDescriptors: ReadonlyArray<PropertyDescriptor>;
        if (destTable !== undefined && tables !== undefined) {
            const isNative = isNativeTableInAppDescriptionContext(ccc, getTableName(destTable));
            propertyDescriptors = makePropertyDescriptorsForColumns<AddRowActionDescription>(
                withOutputTable(tables, destTable),
                hasUserFeature,
                false,
                isNative,
                true,
                getColumnAssignmentsWithLegacy,
                columns => ({ columnAssignments: columns, columns: undefined })
            )[0];
        } else {
            propertyDescriptors = [];
        }
        return {
            name: "Add Row",
            isLegacy: !hasUserFeature && !getFeatureSetting("addAndSetActions"),
            needsScreenContext: true,
            properties: [
                {
                    kind: PropertyKind.Table,
                    property: { name: "tableName" },
                    label: "Sheet",
                    required: true,
                    getAllowedTables: (_desc, schema) => getTablesWithPrimitiveColumns(schema.tables),
                    section: PropertySection.Source,
                },
                ...propertyDescriptors,
            ],
        };
    }

    public newActionDescription(tables: InputOutputTables): AddRowActionDescription {
        const columnAssignments: ColumnAssignment[] = [];

        for (const c of getPrimitiveNonHiddenColumns(tables.input)) {
            const outputColumn = getTableColumn(tables.output, c.name);
            if (
                outputColumn !== undefined &&
                isColumnNonHidden(outputColumn) &&
                !isComputedColumn(outputColumn) &&
                isPrimitiveType(outputColumn.type)
            ) {
                columnAssignments.push({ destColumn: c.name, value: makeColumnProperty(c.name) });
            }
        }
        return {
            kind: this.kind,
            tableName: makeTableProperty(getTableName(tables.input)),
            columnAssignments,
        };
    }

    public getColumnAssignments(
        ccc: ComponentConfiguratorContext,
        desc: AddRowActionDescription
    ): { assignments: readonly ColumnAssignment[]; table: TableGlideType } | undefined {
        const maybeTable = getTable(desc, ccc);
        if (maybeTable === undefined) return undefined;
        const { tableName, table } = maybeTable;

        const isNative = isNativeTableInAppDescriptionContext(ccc, tableName);

        const unfiltered = getColumnAssignmentsWithLegacy(desc);
        const assignments = unfiltered.filter(a => {
            const c = getTableColumn(table, a.destColumn);
            // Column assignments can end up assigning to user-specific
            // columns, but we don't allow that yet (and we don't let the
            // user set those), so we have to filter them out.
            return c !== undefined && (isNative || c.isUserSpecific !== true);
        });

        return { assignments, table };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: AddRowActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _detailScreenTitle: SerializedComponent | undefined,
        _itemID: SerializedComponent | undefined,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for add row to sheet action");
        }

        const maybeAssignments = this.getColumnAssignments(builder, desc);
        if (maybeAssignments === undefined) return undefined;

        const { table: destTable, assignments } = maybeAssignments;

        const columnValues = builder.makeColumnValues(
            withOutputTable(tables, destTable),
            assignments,
            undefined,
            [],
            screenContext,
            false,
            true
        );
        if (Object.keys(columnValues).length === 0) return undefined;
        return b.addRowToTable("default", getTableName(destTable), b.object(columnValues), undefined, onSuccess);
    }

    public getAllowedDataEdits(
        desc: AddRowActionDescription,
        _parentTables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): AllowedDataEdits | undefined {
        // FIXME: Technically, just calling `super` should be sufficient, i.e. we
        // shouldn't even have to override this method.

        const table = getTable(desc, ccc)?.table;
        if (table === undefined) return undefined;

        const assignments = getColumnAssignmentsWithLegacy(desc);
        return {
            addRowToTable: new DefaultMap(() => new Set(), [[table, new Set(assignments.map(a => a.destColumn))]]),
            setColumnsInRow: new DefaultMap(() => new Set()),
            deleteRow: new Set(),
        };
    }

    public getTokenizedDescription(
        desc: AddRowActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.tableName, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [{ kind: "string", value: "to " }, columnToken];
    }

    public inflate(ib: WireInflationBackend, desc: AddRowActionDescription): WireActionHydrator | undefined {
        const maybeTable = getTable(desc, ib);
        if (maybeTable === undefined) return undefined;
        const { tableName, table } = maybeTable;

        const outputValueGetters = makeValueGettersForTable(ib, getColumnAssignmentsWithLegacy(desc), table);
        if (outputValueGetters.length === 0) return undefined;

        return vp => {
            const outputRow: Row = {
                $rowID: makeRowID(),
                $isVisible: false,
                ...fromPairs(outputValueGetters.map(([n, g]) => [n, g(vp)])),
            };
            return async ab => {
                return ab.addRow(tableName, outputRow);
            };
        };
    }
}
